import { forwardRef, useImperativeHandle, useRef } from 'react';
import { authedFetch } from '../../ai/authedFetch';
import { fetchPollStatus } from '../../utils/pollRetry';
import { capabilityDeniedMessage } from '../../utils/capabilityErrors';
import { readSubmitGrant, sumGrants } from '../../utils/runGrant';
import { GT4SD_MAX_SAMPLES_PER_JOB, splitJobs } from '../../utils/generationBounds';
// Part 1a: every job body comes from the row's payload (src/ai/rows/rowPayloads.js).
import { gt4sdJobBody } from '../../ai/rows/rowPayloads';

const POLL_INTERVAL_MS = 1000;

/**
 * Submit a generation job, poll until done, return the result (D3 durability fix, 2026-08-20:
 * replaces the old single synchronous POST /generate fetch, a lost response, closed tab, or
 * instance death used to lose the entire generation with zero durable trace, see
 * docs/DURABILITY-AUDIT-2026-08-20.md finding D3). Mirrors FM4MRunner's submitAndPoll shape.
 */
async function submitAndPoll({ base, body, signal, activeJobIds, onStatus, authHeaders, onGrant }) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const hdrs = { 'Content-Type': 'application/json', ...authHeaders };

  const submitResp = await authedFetch(`${base}/generate_job`, {
    method: 'POST',
    headers: hdrs,
    body: JSON.stringify(body),
    signal,
  });
  if (!submitResp.ok) {
    let j = null;
    try { j = await submitResp.json(); } catch (_) {}
    const denied = capabilityDeniedMessage(j);
    if (denied) throw new Error(denied);
    let msg = `GT4SD job submit failed: ${submitResp.status}`;
    if (j?.detail) msg += `: ${typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail)}`;
    throw new Error(msg);
  }
  const submitted = await submitResp.json();
  const { job_id } = submitted;
  // 0.3: what the plan's per-run limit granted this job (the submit JSON, or llm-proxy's
  // X-Spectra-* headers when the proxy tallied).
  if (typeof onGrant === 'function') onGrant(readSubmitGrant(submitResp, submitted));
  activeJobIds.current.push({ id: job_id, base, authHeaders });

  const onAbort = () => {
    authedFetch(`${base}/generate_job/${job_id}/cancel`, { method: 'POST', headers: authHeaders }).catch(() => {});
  };
  signal?.addEventListener('abort', onAbort);

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const statusResp = await fetchPollStatus(() => authedFetch(`${base}/generate_job/${job_id}`, { headers: authHeaders, signal }), { signal });
      if (!statusResp.ok) throw new Error(`GT4SD job poll failed: ${statusResp.status}`);
      const status = await statusResp.json();

      if (onStatus && (status.status === 'running' || status.status === 'queued')) {
        onStatus(`GT4SD: generating${status.status === 'queued' ? ' (queued)' : '...'}`);
      }

      if (status.status === 'complete') {
        // GenerateJobResult carries the same fields GenerateResponse did
        // (run_id, algorithm, count, results, canonical_smiles) alongside
        // job_id/status: strip the job-poll-only fields so callers get the
        // same shape the old synchronous /generate response had.
        const { job_id: _jid, status: _st, error: _err, ...data } = status;
        return data;
      }
      if (status.status === 'cancelled') {
        throw new DOMException('Job cancelled', 'AbortError');
      }
      if (status.status === 'error') {
        throw new Error(status.error || 'GT4SD generation failed');
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
    activeJobIds.current = activeJobIds.current.filter((j) => j.id !== job_id);
  }
}

/**
 * Merge the poll results of the jobs one GT4SD call was split into (0.4) into the single
 * response shape ResultsTable reads: results and canonical_smiles concatenated in job order
 * (a job with no canonical_smiles contributes its raw results so the two stay aligned),
 * count recomputed, run_id kept from the first job and every job's id listed in run_ids.
 * `failedJobs` lists the jobs that failed as { index, candidates, message } (index = the
 * job's position in the split, candidates = its n_samples), the per-job failure shape the
 * MatterGen runner reports too (spec 0.7); it is copied to `failed_jobs` only when
 * non-empty. One job with no failures is returned exactly as the service sent it.
 */
export function mergeGt4sdResults(parts, failedJobs = []) {
  const failed = Array.isArray(failedJobs) ? failedJobs : [];
  if (parts.length === 1 && failed.length === 0) return parts[0];
  const resultsOf = (p) => (Array.isArray(p.results) ? p.results : []);
  const results = parts.flatMap(resultsOf);
  const anyCanonical = parts.some((p) => Array.isArray(p.canonical_smiles));
  const merged = {
    ...parts[0],
    count: results.length,
    results,
    run_ids: parts.map((p) => p.run_id).filter(Boolean),
  };
  if (anyCanonical) merged.canonical_smiles = parts.flatMap((p) => (Array.isArray(p.canonical_smiles) ? p.canonical_smiles : resultsOf(p)));
  else delete merged.canonical_smiles;

  // GT4SD RT (Part D, D-D4): `properties` is index-parallel to `results`, so it has to
  // be concatenated with them rather than inherited from the first job. Spreading
  // ...parts[0] reported job 1's measurements against every job's molecules: counts
  // still matched, values still looked plausible, and each molecule was labelled with
  // another molecule's logP. A job that measured nothing contributes one null per
  // molecule, so a later job's measurements never slide into its positions.
  // Each part contributes exactly one slot per molecule IT returned, taken from its own
  // properties where there is one and null where there is not. Concatenating a part's
  // properties at their own length is not enough: a part with a short list (or an empty
  // one, which is still an array) shifts every later part's measurements onto the wrong
  // molecules, and a part with a long one spills into the next part's positions.
  if (parts.some((p) => Array.isArray(p.properties))) {
    merged.properties = parts.flatMap((p) => resultsOf(p).map((_, index) => (
      Array.isArray(p.properties) && p.properties[index] !== undefined ? p.properties[index] : null
    )));
  } else {
    delete merged.properties;
  }

  // The RT run meta is per-job too. Sums are over the jobs that ran: a failed job
  // charged nothing and measured nothing, so it is absent from `parts` and from every
  // total here, and is reported separately as failed_jobs.
  const sum = (key) => {
    const present = parts.filter((p) => typeof p[key] === 'number');
    return present.length > 0 ? present.reduce((total, p) => total + p[key], 0) : undefined;
  };
  for (const key of ['charged', 'returned', 'shortfall', 'hits_within_tolerance']) {
    const total = sum(key);
    if (total === undefined) delete merged[key]; else merged[key] = total;
  }
  const elapsed = sum('elapsed_s');
  if (elapsed === undefined) delete merged.elapsed_s;
  else merged.elapsed_s = Math.round(elapsed * 100) / 100;
  if (parts.some((p) => 'partial' in p)) merged.partial = parts.some((p) => p.partial === true);
  if (parts.some((p) => 'cap_hit' in p)) merged.cap_hit = parts.map((p) => p.cap_hit).find(Boolean) || null;

  if (failed.length > 0) merged.failed_jobs = failed.map((f) => ({ ...f }));
  return merged;
}

const GT4SDRunner = forwardRef(function GT4SDRunner({ onStatus, baseUrl, authHeaders }, ref) {
  const activeJobIds = useRef([]);

  useImperativeHandle(ref, () => ({
    // Part 1a (contract 1.6): `payload` is gt4sdPayloadForRow(row) (src/ai/rows/rowPayloads.js).
    // Every job body is gt4sdJobBody(payload, n): the algorithm, its algorithm_version and
    // the RT fields, all from the row. The runner reads nothing else. opts = { onProgress,
    // signal, baseUrl, authHeaders }.
    async execute(payload, opts) {
      const options = opts || {};
      const p = payload || {};
      const nSamples = Math.floor(Number(p.candidates));

      const base = options.baseUrl || baseUrl || process.env.REACT_APP_GT4SD_API || 'http://localhost:8014';
      const effAuthHeaders = options.authHeaders !== undefined ? options.authHeaders : authHeaders;

      if (onStatus) onStatus(`Generating with GT4SD (${p.algorithm})...`);

      // 0.4 (spec 2026-10-07): the service rejects n_samples above 200 with 422, so a
      // larger request runs as several jobs of at most GT4SD_MAX_SAMPLES_PER_JOB, one after
      // another, merged into one result. A failed job keeps the other jobs' molecules;
      // only when every job fails is the first error rethrown.
      const jobSizes = splitJobs(nSamples, GT4SD_MAX_SAMPLES_PER_JOB);
      // 0.3: each accepted job's grant, summed onto the one merged run row below.
      const grants = [];
      const onGrant = (g) => grants.push(g);
      const parts = [];
      const failedJobs = [];
      let firstError = null;
      let done = 0;
      for (let j = 0; j < jobSizes.length; j += 1) {
        try {
          const part = await submitAndPoll({ base, body: gt4sdJobBody(p, jobSizes[j]), signal: options.signal, activeJobIds, onStatus, authHeaders: effAuthHeaders, onGrant });
          parts.push(part);
          done += Array.isArray(part.results) ? part.results.length : 0;
          if (typeof options.onProgress === 'function') {
            options.onProgress({ key: 'gt4sd', done, total: nSamples, label: 'small_molecule' });
          }
        } catch (e) {
          // A user cancel must keep its AbortError identity: useToolsRunner.js
          // checks err?.name === 'AbortError' to distinguish a deliberate stop
          // from a real failure. Rewrapping it into a plain Error here would
          // swallow the cancel, let runRows() resolve normally, and leave
          // AppProvider marking the run doc 'complete' instead of 'cancelled'.
          if (e?.name === 'AbortError') throw e;
          if (!firstError) firstError = e;
          failedJobs.push({ index: j, candidates: jobSizes[j], message: String(e?.message || e) });
          console.warn(`GT4SD: job ${j + 1} of ${jobSizes.length} failed; continuing with the rest.`, e);
        }
      }
      if (parts.length === 0) {
        throw new Error(firstError?.message ? `GT4SD error: ${firstError.message}` : 'GT4SD generation failed');
      }
      const data = mergeGt4sdResults(parts, failedJobs);

      const result = {
        ok: true,
        total_requested: nSamples,
        // Part 1a (D19): the run names its row. `request` is the body with the row's whole
        // count. `base`: where this job ran; the pooling gate (spec 2026-10-07 Part 0.5) reads it.
        runs: [{ rowId: p.rowId ?? null, target: p.algorithm ?? null, customModelId: p.customModelId ?? null, request: gt4sdJobBody(p, nSamples), response: data, base }],
      };
      // 0.3: what the plan granted, summed across this row's jobs, on its one run row.
      Object.assign(result.runs[0], sumGrants(grants));
      return result;
    },

    /** Cancel all active GT4SD jobs on the backend. */
    cancelAll() {
      for (const j of activeJobIds.current) {
        authedFetch(`${j.base}/generate_job/${j.id}/cancel`, { method: 'POST', headers: j.authHeaders }).catch(() => {});
      }
      activeJobIds.current = [];
    },
  }));

  return null;
});

export default GT4SDRunner;
